/**
 * AI CONTEXT
 * Purpose: quick-note text input with submit state machine and minimize UI.
 * Inputs: async submit callback from QuickNotes container.
 * Used by: QuickNotes.
 * Refactor: extract submit status state machine into hook for reuse/testing.
 */
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  DownOutlined,
  LoadingOutlined,
  SendOutlined,
  UpOutlined,
} from "@ant-design/icons"
import { Button } from "antd"
import TextArea, { TextAreaRef } from "antd/es/input/TextArea"
import { motion } from "motion/react"
import { useRef, useState } from "react"

import Flex from "#blocks/Flex"

const SubmitButtonIcon = ({
  status,
}: {
  status: "success" | "error" | "pending" | "idle"
}) => {
  switch (status) {
    case "success":
      return <CheckCircleTwoTone twoToneColor={"#52c41a"} />
    case "error":
      return <CloseCircleTwoTone twoToneColor={"#f5222d"} />
    case "pending":
      return <LoadingOutlined />
    default:
      return <SendOutlined />
  }
}

enum SubmitButtonStatus {
  IDLE = "idle",
  PENDING = "pending",
  SUCCESS = "success",
  ERROR = "error",
}

const QuickNotesInput = ({
  onSubmit,
}: {
  onSubmit: (note: string) => Promise<string>
}) => {
  const [newNote, setNewNote] = useState("")
  const [isMinimized, setIsMinimized] = useState(false)
  const [status, setStatus] = useState<SubmitButtonStatus>(
    SubmitButtonStatus.IDLE
  )

  const inputRef = useRef<TextAreaRef>(null)

  const onToggleMinimize = () => {
    setIsMinimized(!isMinimized)
  }

  const handleSubmit = async () => {
    if (!newNote) return

    setStatus(SubmitButtonStatus.PENDING)

    try {
      const id = await onSubmit(newNote)
      setNewNote("")

      if (id) {
        setStatus(SubmitButtonStatus.SUCCESS)
      } else {
        setStatus(SubmitButtonStatus.ERROR)
      }
    } catch (error) {
      console.error("Error submitting quick note:", error)
      setStatus(SubmitButtonStatus.ERROR)
    } finally {
      setTimeout(() => {
        setStatus(SubmitButtonStatus.IDLE)
      }, 750)
    }
  }

  const newLineCount = (newNote.match(/\n/g) || []).length

  return (
    <Flex
      style={{
        alignItems: "flex-end",
        justifyContent: "space-between",
        width: 280,
      }}
      gap={10}
    >
      <Flex
        col
        style={{
          width: "100%",
          position: "relative",
        }}
      >
        <motion.div
          animate={{
            height: isMinimized ? 40 : "auto",
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
          style={{ overflow: "hidden" }}
        >
          <TextArea
            autoSize
            maxLength={200}
            ref={inputRef}
            placeholder="What's on your mind?"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
        </motion.div>
        {(newNote.length > 50 || newLineCount > 2) && (
          <Button
            style={{
              fontSize: 8,
              textAlign: "right",
              height: "fit-content",
              padding: "2px 6px",
              width: "fit-content",
              position: "absolute",
              right: 2,
              top: 2,
            }}
            onClick={onToggleMinimize}
          >
            <span>{isMinimized ? "Maximize" : "Minimize"}</span>
            {isMinimized ? <UpOutlined /> : <DownOutlined />}
          </Button>
        )}
      </Flex>
      <Button
        style={{ fontSize: 12 }}
        onClick={handleSubmit}
        disabled={status === SubmitButtonStatus.PENDING}
      >
        <SubmitButtonIcon status={status} />
      </Button>
    </Flex>
  )
}

export default QuickNotesInput
